import { TransactionType } from '@/api/Agency/agency.interface'
import { User } from '@/api/Auth/auth.interface'
import { Currencies } from '../interfaces'
import { Services } from '../services.enum'

export type TopUpStatus =
  | 'Pending'
  | 'Success'
  | 'Failed'
  | 'Processing'
  | 'Sent'
  | 'Revoked'
  | 'Canceled'
  | 'Hold'

export interface CreateTopUpDTO {
  topup_requests: TopUp[]
}

export interface TopUp {
  phone_number: string
  destination_account?: string
  client: string
  count: number
  product: string | number
  code: string
  confirmation_account?: string
}

export interface GetTopUpDTO extends TopUp {
  uuid: string
  status: TopUpStatus
  topup: string
  retry: number
  can_retry: boolean
  history: HistoryItem[]
  created_at: string
  carrier_trans_id: string
  trans_id: string
  destination_amount: string
  price: string
}

export interface TopupRequests {
  topup_requests: TopUp[]
}

export interface TopUpAgent extends User {
  topup_prices: TopUpPrice[]
  topup_credit_uuid: string
  topup_credit: number
  description?: string
}

export interface TopUpPrice {
  agent: string
  cost?: number | string
  product: string | number
  is_active: boolean
  price?: number | string
  currency: 'USD' | 'CUP'
}

export interface TopUpPlatformPrice {
  agency: string
  min_amount: number
  max_amount: number
  percent: number
  can_deliver: boolean
  is_active: boolean
}

export type CreateAgentConfigDTO = TopUpPrice

export interface GetTopUpPriceDTO extends TopUpPrice {
  uuid: string
  shared_provider?: string
}

export interface AgencyTopUpPrice extends TopUpPrice {
  product: string
  provider: string
  provider_name: string
  agency: string
  agency_name: string
  price: number
  is_active: boolean
  rate: number
  uuid: string
  shared_provider?: string
}

export interface Provider {
  name: string
  description: string
  data: string
  key: string
  uuid: string
  provider_credit: string
  configured: boolean
}

export interface SharedProvider {
  name: string
  parent_shared: null | string
  parent_provider: null | string
  agency: string
  is_active: boolean
  can_share: boolean
}

export interface GetProviderDTO extends Provider {
  uuid: string
  agency: string
}

export interface Product {
  name: string
  amount: number
  validation_regex: string
  code: string
  destination_amount: string
  best_seller: boolean
  best_seller_message: string
}

export interface GetProductDTO extends Product {
  uuid: string
  full_code: string
  created_at: string
  updated_at: string
  used_by_subagents: boolean
  destination_amount: string
}

export type CreateProductDTO = Product

export interface ProductProviderCode {
  product: string
  provider: string
  code: string
}

export interface GetProductProviderCodeDTO extends ProductProviderCode {
  uuid: string
  product_name: string
  provider_name: string
  created_at: string
  updated_at: string
}

export interface AgentCredit {
  agent: string
  credit: number
}

export interface GetAgentCreditDTO extends AgentCredit {
  uuid: string
  agent: string
  agent_name: string
  credit: number
  created_at: string
  updated_at: string
}

export interface TransferTopUpCredit {
  from_agent: string
  to_agent: string
  credit: number
  description?: string
}

export interface ProviderCredit {
  uuid: string
  name: string
  real_credit: string
}

export interface Transaction {
  uuid: string
  created_by: string
  origin: string
  destiny: string
  amount: number
  description: string
  created_at: string
  updated_at: string
  phone_number: string
  topup_amount: string
  topup_provider: string
  type: TransactionType
}

export interface ProviderConfig {
  uuid: string
  agency: string
  created_at: string
  updated_at: string
}

export interface UpdateSharedProviderCreditDTO {
  provider: string
  credit: number
  description: string
}

export interface UpdateProviderCreditDTO extends UpdateSharedProviderCreditDTO {
  agency: string
}

export interface TopUpDeal {
  title: string
  image: string
  description: string
  start_date: string
  end_date: string
  agency: string
  uuid?: string
  promotion: boolean
}

export interface Report {
  labels: string[]
  topups: number[]
  CUP: CurrencyDataset
  USD: CurrencyDataset
  totals: {
    CUP: ReportCurrencyTotal
    USD: ReportCurrencyTotal
    topups: number
    sent: number
    processing: number
    pending: number
    success: number
    failed: number
  }
}

interface ReportCurrencyTotal {
  earnings: number
  total_cost: number
  total_price: number
}

interface CurrencyDataset {
  earnings: number[]
  total_price: number[]
  total_cost: number[]
}

export interface HistoryItem {
  date: string
  status: TopUpStatus
  details?: string
  code?: number
}

export interface RemoveCreditDTO {
  credit: string
  net_credit: number
}

export interface TopUpClientPhones {
  phone: string
  last_used: string
}

export interface TopUpClient {
  uuid: string
  first_name: string
  last_name: string
  phone: string
  topup_phones: TopUpClientPhones[]
  agency: string
  agency_name: string
  created_at: string
  updated_at: string
}

export interface CreateTopUpClientDTO {
  first_name: string
  last_name: string
  phone: string
  topup_phones: {}
}

export interface BasicTransaction {
  uuid: string
  created_by: string
  origin: string
  service: Services.Topup
  destiny: string
  amount: string
  description: string
  currency: Currencies
  created_at: string
  updated_at: string
}
export interface TopUpCreditTransaction extends BasicTransaction {
  type: TopUpCreditTransactionTypes
}

export interface TopUpBalanceTransaction extends BasicTransaction {
  type: TopUpBalanceTransactionTypes
}

export interface TopUpProviderCreditTransaction extends BasicTransaction {
  type: TopUpProviderCreditTransactionTypes
}

export interface TopUpProviderCreditTransaction extends BasicTransaction {
  type: TopUpProviderCreditTransactionTypes
}

export interface TopUpAgencyTransaction extends BasicTransaction {
  type: TopUpAgencyTransactionTypes
}

export enum TopUpCreditTransactionTypes {
  ADD_CREDIT = 'ADD_CREDIT',
  REMOVE_CREDIT = 'REMOVE_CREDIT',
  CONSUME_CREDIT = 'CONSUME_CREDIT',
  REFUND_CREDIT = 'REFUND_CREDIT',
}

export enum TopUpBalanceTransactionTypes {
  BALANCE_CHARGE = 'BALANCE_CHARGE',
  BALANCE_PAYMENT = 'BALANCE_PAYMENT',
}

export enum TopUpProviderCreditTransactionTypes {
  ADD_PROVIDER_CREDIT = 'ADD_PROVIDER_CREDIT',
  REMOVE_PROVIDER_CREDIT = 'REMOVE_PROVIDER_CREDIT',
  CONSUME_PROVIDER_CREDIT = 'CONSUME_PROVIDER_CREDIT',
  REFUND_PROVIDER_CREDIT = 'REFUND_PROVIDER_CREDIT',
}

export enum TopUpAgencyTransactionTypes {
  TOPUP_CREATE = 'TOPUP_CREATE',
  TOPUP_REFUND = 'TOPUP_REFUND',
}
